/**
 * Submission Session Manager
 * Handles all submission session data via localStorage and manages session across steps
 * Session ID is passed as GET parameter between pages
 *
 * Features:
 * - Multi-session support: Track multiple submission sessions simultaneously
 * - Auto-cleanup: Removes sessions older than 72 hours automatically
 * - Session switching: Switch between different submission sessions
 * - Console helpers: Use SessionHelper.help() for management commands
 *
 * Console Usage:
 *   SessionHelper.list()      - List all active sessions
 *   SessionHelper.switch(#)   - Switch to session by index
 *   SessionHelper.delete(#)   - Delete session by index
 *   SessionHelper.cleanup()   - Clean up expired sessions
 *   SessionHelper.current()   - Show current session info
 */
(function () {
  "use strict";

  // One manager per page. This file ships in modules.bundle.js; a second
  // copy (a page <script> tag) used to replace the instance the step module
  // had already captured, each instance minted its own uuid, and step 1 was
  // saved under both (docs/agent-failure-tally.md D54).
  if (window.SubmissionSessionManager) {
    return;
  }

  // Constants
  const SESSION_KEY_PREFIX = "submission_session_";
  const SESSION_ID_KEY = "current_submission_id";
  const SUBMITTED_SESSIONS_KEY = "submitted_session_ids";
  const SESSION_EXPIRY_HOURS = 72; // Sessions expire after 72 hours (3 days)

  class SubmissionSessionManager {
    constructor() {
      this.sessionId = null;
      this.sessionData = null;
      this.init();
    }

    /**
     * Initialize the session manager
     */
    init() {
      // Try to get session ID from URL parameter first
      const urlParams = new URLSearchParams(window.location.search);
      const urlSessionId = urlParams.get("session_id");

      if (urlSessionId) {
        // Use session from URL
        this.sessionId = urlSessionId;
        localStorage.setItem(SESSION_ID_KEY, urlSessionId);
      } else {
        // Try to get from localStorage or create new
        this.sessionId =
          localStorage.getItem(SESSION_ID_KEY) || this.createNewSession();
      }

      // Load session data
      this.loadSession();

      // Clean up expired sessions
      this.cleanupExpiredSessions();
    }

    /**
     * Create a new session
     *
     * Note: this writes the new id to localStorage but leaves this.sessionId
     * alone, so a page keeps the uuid it loaded with even after the IMDb step
     * calls clearSession() + initializeSession(). Assigning it here would give
     * every IMDb-page visit a new uuid, and a new submissions row on Confirm.
     *
     * @returns {string} Session ID
     */
    createNewSession() {
      const sessionId = this.generateUUID();
      localStorage.setItem(SESSION_ID_KEY, sessionId);

      // Initialize empty session data
      this.sessionData = {
        id: sessionId,
        created_at: Date.now(),
        updated_at: Date.now(),
        current_step: 1,
        completed_steps: [],
        data: {
          step1: {}, // IMDB/Basic info
          step2: {}, // Upload
          step3: {}, // Customize
          step4: {}, // Review
        },
      };

      this.saveSession();
      return sessionId;
    }

    /**
     * Generate UUID for session ID
     * @returns {string}
     */
    generateUUID() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (
        c
      ) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    }

    /**
     * Load session from localStorage
     */
    loadSession() {
      const sessionKey = SESSION_KEY_PREFIX + this.sessionId;
      const storedData = localStorage.getItem(sessionKey);

      if (storedData) {
        try {
          this.sessionData = JSON.parse(storedData);

          // Check if session is expired
          if (this.isSessionExpired()) {
            this.createNewSession();
          }
        } catch (e) {
          console.error("Failed to parse session data:", e);
          this.createNewSession();
        }
      } else {
        this.createNewSession();
      }
    }

    /**
     * Save session to localStorage
     */
    saveSession() {
      const sessionKey = SESSION_KEY_PREFIX + this.sessionId;
      this.sessionData.updated_at = Date.now();

      try {
        const jsonData = JSON.stringify(this.sessionData);
        localStorage.setItem(sessionKey, jsonData);
      } catch (e) {
        console.error("[SessionManager] ❌ Failed to save session data:", e);

        // Try to clear old sessions if storage is full
        if (e.name === "QuotaExceededError") {
          console.warn(
            "[SessionManager] ⚠️ LocalStorage quota exceeded! Attempting cleanup..."
          );

          const jsonData = JSON.stringify(this.sessionData);
          const sizeKB = (jsonData.length / 1024).toFixed(2);
          console.log(
            `[SessionManager] Attempted to save ${sizeKB} KB of data`
          );

          this.cleanupExpiredSessions();

          // Try again
          try {
            localStorage.setItem(sessionKey, JSON.stringify(this.sessionData));
            console.log("[SessionManager] ✅ Session saved after cleanup");
          } catch (e2) {
            console.error(
              "[SessionManager] ❌ Still failed after cleanup:",
              e2
            );
            console.error(
              "[SessionManager] ⚠️ DATA MAY NOT BE SAVED! Consider reducing image sizes."
            );

            // Log which fields are taking up space
            this.logStorageUsage();

            throw new Error(
              "Failed to save session: localStorage quota exceeded"
            );
          }
        } else {
          throw e;
        }
      }
    }

    /**
     * Log storage usage breakdown to help debug quota issues
     */
    logStorageUsage() {
      console.log("[SessionManager] Storage usage breakdown:");

      const stepSizes = {};
      for (let i = 1; i <= 5; i++) {
        const stepData = this.getStepData(i);
        const stepJson = JSON.stringify(stepData);
        stepSizes[`step${i}`] = (stepJson.length / 1024).toFixed(2);

        // Check for large fields
        for (const [key, value] of Object.entries(stepData)) {
          if (typeof value === "string" && value.length > 10000) {
            console.log(
              `  - step${i}.${key}: ${(value.length / 1024).toFixed(2)} KB`
            );
          } else if (typeof value === "object" && value !== null) {
            const valueJson = JSON.stringify(value);
            if (valueJson.length > 10000) {
              console.log(
                `  - step${i}.${key}: ${(valueJson.length / 1024).toFixed(
                  2
                )} KB (object)`
              );
            }
          }
        }
      }

      console.log("[SessionManager] Step sizes:", stepSizes);
    }

    /**
     * Check if session is expired
     * @returns {boolean}
     */
    isSessionExpired() {
      if (!this.sessionData || !this.sessionData.created_at) {
        return true;
      }

      const expiryTime = SESSION_EXPIRY_HOURS * 60 * 60 * 1000;
      const sessionAge = Date.now() - this.sessionData.created_at;

      return sessionAge > expiryTime;
    }

    /**
     * Clean up expired sessions from localStorage
     * @returns {number} Number of sessions cleaned up
     */
    cleanupExpiredSessions() {
      const expiryTime = SESSION_EXPIRY_HOURS * 60 * 60 * 1000;
      const now = Date.now();
      let cleanedCount = 0;

      // Get all localStorage keys
      const keys = Object.keys(localStorage);

      keys.forEach((key) => {
        if (key.startsWith(SESSION_KEY_PREFIX)) {
          try {
            const data = JSON.parse(localStorage.getItem(key));

            if (data && data.created_at) {
              const age = now - data.created_at;

              if (age > expiryTime) {
                localStorage.removeItem(key);
                cleanedCount++;
                console.log(
                  `[SessionManager] Removed expired session: ${key} (age: ${Math.round(
                    age / 3600000
                  )} hours)`
                );
              }
            }
          } catch (e) {
            // Invalid data, remove it
            localStorage.removeItem(key);
            cleanedCount++;
            console.log(
              `[SessionManager] Removed invalid session data: ${key}`
            );
          }
        }
      });

      if (cleanedCount > 0) {
        console.log(
          `[SessionManager] Cleaned up ${cleanedCount} old session(s)`
        );
      }

      return cleanedCount;
    }

    /**
     * Get all active sessions from localStorage
     * @returns {Array} Array of session objects with metadata
     */
    getAllSessions() {
      const sessions = [];
      const keys = Object.keys(localStorage);
      const now = Date.now();

      keys.forEach((key) => {
        if (key.startsWith(SESSION_KEY_PREFIX)) {
          try {
            const data = JSON.parse(localStorage.getItem(key));

            if (data && data.id) {
              const age = now - data.created_at;
              const ageHours = Math.round(age / 3600000);
              const ageDays = Math.round(age / 86400000);

              // Get title info from step 1 if available
              const titleName = data.data?.step1?.title_name || "Untitled";
              const imdbId = data.data?.step1?.imdb_id || null;

              sessions.push({
                id: data.id,
                created_at: data.created_at,
                updated_at: data.updated_at,
                current_step: data.current_step,
                completed_steps: data.completed_steps,
                title_name: titleName,
                imdb_id: imdbId,
                age_hours: ageHours,
                age_days: ageDays,
                is_current: data.id === this.sessionId,
              });
            }
          } catch (e) {
            console.error(`[SessionManager] Error parsing session ${key}:`, e);
          }
        }
      });

      // Sort by updated_at (most recent first)
      sessions.sort((a, b) => b.updated_at - a.updated_at);

      return sessions;
    }

    /**
     * Switch to a different session
     * @param {string} sessionId - ID of session to switch to
     * @returns {boolean} Success status
     */
    switchToSession(sessionId) {
      const sessionKey = SESSION_KEY_PREFIX + sessionId;
      const sessionData = localStorage.getItem(sessionKey);

      if (!sessionData) {
        console.error(`[SessionManager] Session not found: ${sessionId}`);
        return false;
      }

      try {
        // Validate the session data
        const data = JSON.parse(sessionData);
        if (!data.id || data.id !== sessionId) {
          console.error(
            `[SessionManager] Invalid session data for: ${sessionId}`
          );
          return false;
        }

        // Switch to the new session
        this.sessionId = sessionId;
        this.sessionData = data;
        localStorage.setItem(SESSION_ID_KEY, sessionId);

        console.log(`[SessionManager] Switched to session: ${sessionId}`);
        return true;
      } catch (e) {
        console.error(
          `[SessionManager] Error switching to session ${sessionId}:`,
          e
        );
        return false;
      }
    }

    /**
     * Delete a specific session
     * @param {string} sessionId - ID of session to delete
     * @param {boolean} force - Force delete even if it's the current session
     * @returns {boolean} Success status
     */
    deleteSession(sessionId, force = false) {
      // Prevent deleting current session unless forced
      if (sessionId === this.sessionId && !force) {
        console.warn(
          `[SessionManager] Cannot delete current session without force flag`
        );
        return false;
      }

      const sessionKey = SESSION_KEY_PREFIX + sessionId;
      const existed = localStorage.getItem(sessionKey) !== null;

      if (existed) {
        localStorage.removeItem(sessionKey);
        console.log(`[SessionManager] Deleted session: ${sessionId}`);

        // If we deleted the current session, create a new one
        if (sessionId === this.sessionId) {
          this.createNewSession();
        }

        return true;
      }

      return false;
    }

    /**
     * Get data for a specific step
     * @param {number} step - Step number (1-4)
     * @returns {object}
     */
    getStepData(step) {
      const stepKey = `step${step}`;
      const data = this.sessionData?.data?.[stepKey];

      if (!data) {
        return {};
      }

      // Return a deep copy to prevent accidental mutations
      const dataCopy = JSON.parse(JSON.stringify(data));

      // Decode HTML entities in string values (handles old cached data with &apos; etc.)
      if (window.HtmlEntityDecoder) {
        return window.HtmlEntityDecoder.decodeRecursive(dataCopy);
      }

      return dataCopy;
    }

    /**
     * Save data for a specific step
     * @param {number} step - Step number (1-4)
     * @param {object} data - Data to save
     * @param {boolean} merge - Whether to merge with existing data (default: true)
     */
    saveStepData(step, data, merge = true) {
      const stepKey = `step${step}`;

      if (!this.sessionData.data) {
        this.sessionData.data = {};
      }

      if (merge && this.sessionData.data[stepKey]) {
        // Merge with existing data
        this.sessionData.data[stepKey] = {
          ...this.sessionData.data[stepKey],
          ...data,
        };
      } else {
        // Replace data
        this.sessionData.data[stepKey] = data;
      }

      // Mark step as completed if not already
      if (!this.sessionData.completed_steps.includes(step)) {
        this.sessionData.completed_steps.push(step);
      }

      // Update current step
      this.sessionData.current_step = step;

      this.saveSession();
    }

    /**
     * Get specific field value from any step
     * @param {string} field - Field name
     * @param {number} step - Step number (optional, searches all if not provided)
     * @returns {*}
     */
    getField(field, step = null) {
      if (step) {
        return this.getStepData(step)[field];
      }

      // Search all steps
      for (let i = 1; i <= 4; i++) {
        const stepData = this.getStepData(i);
        if (stepData && field in stepData) {
          return stepData[field];
        }
      }

      return null;
    }

    /**
     * Set specific field value
     * @param {string} field - Field name
     * @param {*} value - Field value
     * @param {number} step - Step number
     */
    setField(field, value, step) {
      const stepData = this.getStepData(step);
      stepData[field] = value;
      this.saveStepData(step, stepData);
    }

    /**
     * Get all session data
     * @returns {object}
     */
    getAllData() {
      return this.sessionData;
    }

    /**
     * Get session ID
     * @returns {string}
     */
    getSessionId() {
      return this.sessionId;
    }

    /**
     * Navigate to next step with session ID
     * @param {string} path - Path to navigate to
     */
    navigateWithSession(path) {
      const separator = path.includes("?") ? "&" : "?";
      window.location.href = `${path}${separator}session_id=${this.sessionId}`;
    }

    /**
     * Clear current session
     */
    clearSession() {
      const sessionKey = SESSION_KEY_PREFIX + this.sessionId;
      localStorage.removeItem(sessionKey);
      localStorage.removeItem(SESSION_ID_KEY);
      this.createNewSession();
    }

    /**
     * Mark a session as submitted to prevent re-submission
     * @param {string} sessionId - Session ID to mark as submitted (defaults to current)
     */
    markAsSubmitted(sessionId = null) {
      const idToMark = sessionId || this.sessionId;

      try {
        const submittedSessions = this.getSubmittedSessions();

        // Add to submitted list if not already there
        if (!submittedSessions.includes(idToMark)) {
          submittedSessions.push(idToMark);
          localStorage.setItem(
            SUBMITTED_SESSIONS_KEY,
            JSON.stringify(submittedSessions)
          );
          console.log(
            `[SessionManager] Marked session as submitted: ${idToMark}`
          );
        }
      } catch (e) {
        console.error(
          "[SessionManager] Failed to mark session as submitted:",
          e
        );
      }
    }

    /**
     * Check if a session has been submitted
     * @param {string} sessionId - Session ID to check (defaults to current)
     * @returns {boolean}
     */
    isSubmitted(sessionId = null) {
      const idToCheck = sessionId || this.sessionId;
      const submittedSessions = this.getSubmittedSessions();
      return submittedSessions.includes(idToCheck);
    }

    /**
     * Get list of submitted session IDs
     * @returns {Array<string>}
     */
    getSubmittedSessions() {
      try {
        const stored = localStorage.getItem(SUBMITTED_SESSIONS_KEY);
        return stored ? JSON.parse(stored) : [];
      } catch (e) {
        console.error(
          "[SessionManager] Failed to parse submitted sessions:",
          e
        );
        return [];
      }
    }

    /**
     * Check if current session is submitted and redirect if so
     * @param {string} redirectUrl - URL to redirect to (default: /partners/apply/imdb)
     * @returns {boolean} True if redirected, false otherwise
     */
    checkAndRedirectIfSubmitted(redirectUrl = null) {
      const fallbackRedirectUrl =
        redirectUrl ||
        `${
          window.submissionApplyBasePath || "/partners/bloodstream/apply"
        }/imdb`;
      if (this.isSubmitted()) {
        console.log(
          `[SessionManager] Session ${this.sessionId} already submitted, redirecting...`
        );

        // Clear the entire session data from localStorage
        const sessionKey = SESSION_KEY_PREFIX + this.sessionId;
        localStorage.removeItem(sessionKey);
        localStorage.removeItem(SESSION_ID_KEY);

        console.log(
          `[SessionManager] Cleared submitted session data from localStorage`
        );

        // Show notification before redirect
        if (
          window.showNotification &&
          typeof window.showNotification === "function"
        ) {
          window.showNotification(
            "This submission has already been submitted. Starting a new submission.",
            "info"
          );
        }

        // Redirect immediately without URL params - just the base path
        window.location.href = fallbackRedirectUrl;

        return true;
      }

      return false;
    }

    /**
     * Submit final data to server
     * @returns {Promise}
     */
    async submitFinalData() {
      // Collect all data from all steps
      const submissionData = {
        session_id: this.sessionId,
        ...this.sessionData.data,
      };

      try {
        const response = await fetch("/api/submissions/submit-final", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(submissionData),
        });

        const result = await response.json();

        if (result.success) {
          // Clear session after successful submission
          this.clearSession();
        }

        return result;
      } catch (error) {
        console.error("Submission failed:", error);
        throw error;
      }
    }

    /**
     * Validate step data before proceeding
     * @param {number} step - Step number
     * @returns {object} {valid: boolean, errors: array}
     */
    validateStep(step) {
      const stepData = this.getStepData(step);
      const errors = [];

      switch (step) {
        case 1: // IMDB/Basic info validation
          if (!stepData.title_name && !stepData.imdb_id) {
            errors.push("Title name or IMDB ID is required");
          }
          break;

        case 2: // Upload validation
          if (!stepData.trailer_link && !stepData.trailer_uploads) {
            errors.push("Trailer or promo content is required");
          }
          if (!stepData.feature_link && !stepData.feature_uploads) {
            errors.push("Full content is required");
          }
          break;

        case 3: // Customize validation
          // Optional step, no required fields
          break;

        case 4: // Review validation
          if (!stepData.contact_name) {
            errors.push("Contact name is required");
          }
          if (!stepData.contact_email) {
            errors.push("Contact email is required");
          }
          break;
      }

      return {
        valid: errors.length === 0,
        errors: errors,
      };
    }

    /**
     * Get progress percentage
     * @returns {number}
     */
    getProgress() {
      const totalSteps = 4;
      const completedSteps = this.sessionData?.completed_steps?.length || 0;
      return Math.round((completedSteps / totalSteps) * 100);
    }

    /**
     * Initialize a new session (alias for createNewSession for compatibility)
     * @returns {string} Session ID
     */
    initializeSession() {
      return this.createNewSession();
    }

    /**
     * Set current step
     * @param {number} step - Step number
     */
    setCurrentStep(step) {
      if (this.sessionData) {
        this.sessionData.current_step = step;
        this.saveSession();
      }
    }

    /**
     * Mark step as completed
     * @param {number} step - Step number
     */
    markStepCompleted(step) {
      if (
        this.sessionData &&
        !this.sessionData.completed_steps.includes(step)
      ) {
        this.sessionData.completed_steps.push(step);
        this.saveSession();
      }
    }

    /**
     * Check if a valid session exists
     * @returns {boolean}
     */
    hasSession() {
      return (
        this.sessionData !== null &&
        this.sessionId !== null &&
        !this.isSessionExpired()
      );
    }
  }

  // Create global instance
  window.SubmissionSessionManager = new SubmissionSessionManager();

  // Also expose the class for testing
  window.SubmissionSessionManagerClass = SubmissionSessionManager;

  // Console helper for managing sessions
  window.SessionHelper = {
    /**
     * List all active sessions with details
     */
    list() {
      const sessions = window.SubmissionSessionManager.getAllSessions();

      if (sessions.length === 0) {
        console.log("No active sessions found");
        return [];
      }

      console.log(`Found ${sessions.length} session(s):\n`);

      sessions.forEach((session, index) => {
        const current = session.is_current ? " ⭐ CURRENT" : "";
        const age =
          session.age_days > 0
            ? `${session.age_days}d`
            : `${session.age_hours}h`;

        console.log(`${index + 1}. ${session.title_name}${current}`);
        console.log(`   ID: ${session.id}`);
        console.log(`   Step: ${session.current_step}/5`);
        console.log(`   Age: ${age}`);
        if (session.imdb_id) {
          console.log(`   IMDB: ${session.imdb_id}`);
        }
        console.log("");
      });

      return sessions;
    },

    /**
     * Switch to a specific session by ID or index
     * @param {string|number} sessionIdOrIndex - Session ID or index from list()
     */
    switch(sessionIdOrIndex) {
      let sessionId;

      if (typeof sessionIdOrIndex === "number") {
        // It's an index, get the session ID from the list
        const sessions = window.SubmissionSessionManager.getAllSessions();
        const session = sessions[sessionIdOrIndex - 1]; // 1-based index

        if (!session) {
          console.error(
            `Session index ${sessionIdOrIndex} not found. Use list() to see available sessions.`
          );
          return false;
        }

        sessionId = session.id;
      } else {
        sessionId = sessionIdOrIndex;
      }

      const success = window.SubmissionSessionManager.switchToSession(
        sessionId
      );

      if (success) {
        console.log(`✓ Switched to session: ${sessionId}`);
        console.log("Reload the page to use this session.");
      }

      return success;
    },

    /**
     * Delete a session by ID or index
     * @param {string|number} sessionIdOrIndex - Session ID or index from list()
     */
    delete(sessionIdOrIndex) {
      let sessionId;

      if (typeof sessionIdOrIndex === "number") {
        const sessions = window.SubmissionSessionManager.getAllSessions();
        const session = sessions[sessionIdOrIndex - 1];

        if (!session) {
          console.error(`Session index ${sessionIdOrIndex} not found`);
          return false;
        }

        sessionId = session.id;
      } else {
        sessionId = sessionIdOrIndex;
      }

      const isCurrent =
        sessionId === window.SubmissionSessionManager.getSessionId();

      if (
        isCurrent &&
        !confirm(
          "This is the current session. Are you sure you want to delete it?"
        )
      ) {
        console.log("Delete cancelled");
        return false;
      }

      const success = window.SubmissionSessionManager.deleteSession(
        sessionId,
        true
      );

      if (success) {
        console.log(`✓ Deleted session: ${sessionId}`);
      }

      return success;
    },

    /**
     * Clean up expired sessions (older than 72 hours)
     */
    cleanup() {
      const count = window.SubmissionSessionManager.cleanupExpiredSessions();
      console.log(`✓ Cleaned up ${count} expired session(s)`);
      return count;
    },

    /**
     * Get current session info
     */
    current() {
      const sessionId = window.SubmissionSessionManager.getSessionId();
      const sessions = window.SubmissionSessionManager.getAllSessions();
      const current = sessions.find((s) => s.id === sessionId);

      if (current) {
        console.log("Current Session:");
        console.log(`  Title: ${current.title_name}`);
        console.log(`  ID: ${current.id}`);
        console.log(`  Step: ${current.current_step}/5`);
        console.log(
          `  Age: ${
            current.age_days > 0
              ? current.age_days + "d"
              : current.age_hours + "h"
          }`
        );
        if (current.imdb_id) {
          console.log(`  IMDB: ${current.imdb_id}`);
        }
      }

      return current;
    },

    /**
     * Show help
     */
    help() {
      console.log("Session Management Commands:");
      console.log("");
      console.log(
        "  SessionHelper.list()           - List all active sessions"
      );
      console.log(
        "  SessionHelper.current()        - Show current session info"
      );
      console.log(
        "  SessionHelper.switch(id|#)     - Switch to session by ID or index"
      );
      console.log(
        "  SessionHelper.delete(id|#)     - Delete session by ID or index"
      );
      console.log(
        "  SessionHelper.cleanup()        - Remove expired sessions (>72h)"
      );
      console.log("  SessionHelper.help()           - Show this help");
      console.log("");
      console.log("Examples:");
      console.log(
        "  SessionHelper.list()                                    # List all sessions"
      );
      console.log(
        "  SessionHelper.switch(1)                                 # Switch to first session in list"
      );
      console.log(
        '  SessionHelper.switch("abc-123-def")                     # Switch by ID'
      );
      console.log(
        "  SessionHelper.delete(2)                                 # Delete second session"
      );
      console.log("");
    },
  };

  // SessionHelper available for debugging - type SessionHelper.help() in console
})();
